/**
 * @file seo-errors.ts
 * @description Отказы «Сайт → SEO» словами владельца (phase19.16, партия
 * Р3в, v0.19.64; SB131, SB145, SEO1; db-design-r3v §4, db-design-seo-settings
 * §3).
 *
 * Тексты — реестром «код → фраза» (OCP): новый код сервера — строка здесь.
 * Нет своей фразы — сообщение сервера (он пишет по-русски), нет и его —
 * запасной текст вызывающего. Ошибки, которые относятся к полю окна
 * («Откуда», «Куда»), — по `details.field` сервера (`redirectErrorField`).
 */

import { ApiError } from "../api-client";
import type { PublishCheck } from "../site-view/types";

/** Код ошибки сервера → фраза для человека. */
export const SEO_ERROR_TEXT: Readonly<Record<string, string>> = {
  site_seo_version_conflict: "Настройки SEO изменили в другом окне. Мы загрузили свежие — проверьте и сохраните снова.",
  site_seo_close_confirm_required: "Чтобы закрыть сайт от поисковиков, введите слово «закрыть».",
  site_seo_verification_engine: "Это тег другого поисковика — вставьте его в соседний список.",
  site_seo_verification_invalid: "Не похоже на код подтверждения — скопируйте мета-тег целиком.",
  site_seo_verification_limit: "Не больше 5 кодов на поисковик.",
  site_seo_media_missing: "Этой картинки больше нет в медиатеке — выберите другую.",
  site_seo_media_invalid: "Картинка для ссылок — только фото из медиатеки магазина.",
  site_seo_nothing_pending: "Неопубликованных правок SEO больше нет.",
  site_seo_history_not_found: "Этой записи больше нет — обновите страницу.",
  site_seo_forbidden: "Настройки SEO меняет владелец магазина или сотрудник с правом настроек.",
  site_redirect_from_invalid: "Адрес на этом сайте должен начинаться с «/», например /old-page.",
  site_redirect_from_external: "Это адрес другого сайта — укажите путь на своём, например /old-page.",
  site_redirect_from_service: "Этот адрес занят системой — переадресацию с него сделать нельзя.",
  site_redirect_target_invalid: "Укажите, куда вести: страницу, товар, категорию, статью, рубрику или адрес на этом сайте.",
  site_redirect_target_missing: "Такой страницы на сайте нет — выберите другую цель.",
  site_redirect_target_service: "Вести на служебный адрес нельзя.",
  site_redirect_self: "Адрес не может вести сам на себя.",
  site_redirect_limit: "Лимит 1000 правил; удалите неиспользуемые.",
  site_redirect_not_found: "Этой переадресации больше нет — список обновлён.",
  site_redirects_forbidden: "Переадресации меняет владелец магазина или сотрудник с правом настроек.",
  // «SEO-техника» v0.19.65 (SB148): журнал «Не найдено».
  site_not_found_hidden_limit: "Скрыто уже 5000 адресов — больше нельзя. Верните в список ненужные.",
  site_not_found_path_invalid: "Этот адрес нельзя скрыть: он служебный или раздел сайта.",
  seo_keywords_invalid: "Запрос — до 100 знаков, дополнительных — не больше 20.",
  seo_keywords_not_allowed: "У этой страницы ключевых запросов нет.",
};

/**
 * Коды, у которых сообщение сервера точнее нашей фразы (называет адрес,
 * живую страницу или круг целиком) — показываем его.
 */
const SERVER_TEXT_FIRST: ReadonlySet<string> = new Set(["site_redirect_from_live", "site_redirect_from_taken", "site_redirect_cycle"]);

/** Текст ошибки: своя фраза → сообщение сервера → запасной. */
export function seoErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.code === "network_error") return "Нет связи с сервером. Проверьте интернет и повторите.";
    if (SERVER_TEXT_FIRST.has(err.code) && err.message) return err.message;
    return SEO_ERROR_TEXT[err.code] ?? (err.message || fallback);
  }
  return fallback;
}

/** Код ошибки сервера или null. */
export function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** Поле окна «Добавить переадресацию», к которому относится отказ (`details.field` сервера). */
export function redirectErrorField(err: unknown): "from" | "target" | null {
  if (!(err instanceof ApiError) || !isObject(err.details)) return null;
  if (err.details.field === "fromPath") return "from";
  if (err.details.field === "target") return "target";
  return null;
}

/** 409 `site_redirect_from_taken`: существующее правило из `details.existing` (для «Изменить существующую»). */
export function takenRedirect<T extends { id: string }>(err: unknown): T | null {
  if (!(err instanceof ApiError) || err.code !== "site_redirect_from_taken" || !isObject(err.details)) return null;
  const existing = err.details.existing;
  return isObject(existing) && typeof existing.id === "string" ? (existing as unknown as T) : null;
}

/** Предупреждения записи переадресации — тостом (сохранено, но стоит сказать). */
export const REDIRECT_WARNING_TEXT: Readonly<Record<string, string>> = {
  site_redirect_target_hidden: "Цель сейчас скрыта — переадресация заработает, когда вы её покажете.",
  site_redirect_limit_near: "Ручных переадресаций больше 900 из 1000 — удалите неиспользуемые.",
};

/**
 * Стоп-лист при сохранении (SB58, SEO14, SB145): 409 `site_seo_text_blocked`
 * с проверками в `details.checks` — их показывает галочка владельца SB125.
 * Другая ошибка — пустой список.
 */
export function stopListChecks(err: unknown): PublishCheck[] {
  if (!(err instanceof ApiError) || err.code !== "site_seo_text_blocked") return [];
  if (!isObject(err.details) || !Array.isArray(err.details.checks)) return [{ code: err.code, level: "error", message: err.message }];
  return err.details.checks
    .filter(isObject)
    .filter((c) => typeof c.code === "string" && typeof c.message === "string")
    .map((c) => ({ code: c.code as string, level: "error" as const, message: c.message as string }));
}
